import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { lerp, easeInOut } from '../../lab/shared/PouringCylinder';
import { CuOPowder } from '../../lab/shared/CuOPowder';
import { CottonBud } from '../../lab/shared/CottonBud';

/**
 * Step-specific animations for the CuO+H₂ reduction experiment. Each
 * animation follows the same `active` + `progress` pattern used by the
 * MgO experiment.
 */

interface BaseProps {
  active: boolean;
  progress: number;
}

// World-space layout constants (must match CopperOxideScene3D.tsx)
// The horizontal glass tube hovers above the spirit-lamp flame (which sits
// on the pipeclay triangle on top of a small tripod stand). This puts it
// at roughly stopper-height of the boiling tubes so the rubber tubes
// curve smoothly between them.
const TUBE_HOME_POS: [number, number, number] = [0, 0.35, 0.0];
const TUBE_BALANCE_POS: [number, number, number] = [1.95, 0.13, 0.45];
const CUO_SOURCE_POS: [number, number, number] = [-0.35, 0.30, 0.45];

/**
 * Position of the horizontal glass tube. Mirrors `useCrucibleWorldPosition`
 * from the MgO experiment.
 *  - During a weighing animation the tube arcs from HOME → balance → HOME.
 *  - Otherwise it sits at TUBE_HOME_POS.
 */
type WeighType = 'empty' | 'with-cuo' | 'final' | null;
export function useTubeWorldPosition(
  isWeighing: boolean,
  weighProgress: number,
  weighType: WeighType,
): [number, number, number] {
  if (!isWeighing) return TUBE_HOME_POS;
  const home = TUBE_HOME_POS;
  if (weighProgress < 0.45) {
    const t = easeInOut(weighProgress / 0.45);
    const x = lerp(home[0], TUBE_BALANCE_POS[0], t);
    const z = lerp(home[2], TUBE_BALANCE_POS[2], t);
    const arc = Math.sin(t * Math.PI) * 0.18;
    const y = lerp(home[1], TUBE_BALANCE_POS[1], t) + arc;
    return [x, y, z];
  }
  if (weighProgress < 0.6) {
    return TUBE_BALANCE_POS;
  }
  const t = easeInOut((weighProgress - 0.6) / 0.4);
  const x = lerp(TUBE_BALANCE_POS[0], home[0], t);
  const z = lerp(TUBE_BALANCE_POS[2], home[2], t);
  const arc = Math.sin(t * Math.PI) * 0.18;
  const y = lerp(TUBE_BALANCE_POS[1], home[1], t) + arc;
  return [x, y, z];
}

/**
 * CuO Placement animation — a wooden splinter scoops black powder from a
 * small reservoir (to the left of the workstation) and arcs into the
 * opening of the horizontal glass tube. The pile inside the tube grows
 * via the store's `cuOInTubeG` field (handled in the Crucible-style mesh
 * inside CopperOxideScene3D, not here).
 *
 * Here we only animate the splinter + the falling chunk of powder en
 * route to the tube.
 */
export function CuOPlacementAnimation({ active, progress, cuoMassG }: BaseProps & { cuoMassG: number }) {
  if (!active) return null;
  const t = easeInOut(progress);
  const startPos: [number, number, number] = [CUO_SOURCE_POS[0], CUO_SOURCE_POS[1] + 0.06, CUO_SOURCE_POS[2]];
  const endPos: [number, number, number] = [TUBE_HOME_POS[0] - 0.05, TUBE_HOME_POS[1] + 0.04, TUBE_HOME_POS[2]];
  const x = lerp(startPos[0], endPos[0], t);
  const z = lerp(startPos[2], endPos[2], t);
  const arc = Math.sin(t * Math.PI) * 0.18;
  const y = lerp(startPos[1], endPos[1], t) + arc;
  // Tilt at the end so the powder falls off the splinter
  const tilt = Math.max(0, (progress - 0.55) / 0.45) * 0.9;
  // Powder still on the splinter
  const remaining = Math.max(0, 1 - (progress - 0.55) / 0.45);

  if (progress > 0.92) return null;

  return (
    <group>
      {/* Splinter — a thin wooden stick */}
      <group position={[x, y, z]} rotation={[0, 0, -tilt]}>
        <mesh castShadow>
          <boxGeometry args={[0.07, 0.003, 0.006]} />
          <meshStandardMaterial color="#9c7240" roughness={0.85} />
        </mesh>
        {/* Powder pile on the splinter */}
        {remaining > 0.02 && (
          <mesh position={[0.018, 0.003 + 0.004 * remaining, 0]}>
            <coneGeometry args={[0.012 * Math.sqrt(remaining), 0.008 * remaining, 14]} />
            <meshStandardMaterial color="#1a1a1a" roughness={0.95} />
          </mesh>
        )}
      </group>
      {/* Falling powder cloud — visible as the splinter tilts */}
      {progress > 0.6 && progress < 0.9 && (
        <mesh position={[x + 0.015, y - 0.015, z]}>
          <sphereGeometry args={[0.008 * cuoMassG, 8, 8]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.95} transparent opacity={0.7} />
        </mesh>
      )}
    </group>
  );
}

/**
 * Apparatus build animation — three sequenced sub-animations:
 *   0.00–0.33  pour water into the right boiling tube
 *   0.33–0.66  drop zinc granules into the left boiling tube
 *   0.66–1.00  pour HCl into the left boiling tube
 *
 * We render simple moving meshes (a water stream, falling zinc, an HCl
 * dropper bottle pour) for the user to visually parse the build steps.
 * Final visibility of water/Zn/HCl in the tubes is handled by the store
 * (`waterAdded`, `znAdded`, `hclAdded` flags drive substanceLevel of the
 * boiling tubes in CopperOxideScene3D).
 */
// Top of each boiling tube in WORLD coordinates. These match the placement
// of the BoilingTube components inside CopperOxideScene3D's left and right
// retort-stand groups (parent at y=-0.585, BoilingTube at relative y=0.95,
// tube height = 0.85, so top rim ≈ -0.585 + 0.95 - 0.425 + 0.85 ≈ 0.79).
const LEFT_TUBE_TOP: [number, number, number] = [-1.73, 0.79, -0.2];
const RIGHT_TUBE_TOP: [number, number, number] = [1.63, 0.79, -0.2];

export function ApparatusBuildAnimation({ active, progress }: BaseProps) {
  if (!active) return null;

  // Per-phase local progress (0..1) used to animate beaker tilt + stream width.
  const phase1T = Math.max(0, Math.min(1, progress / 0.33));
  const phase2T = Math.max(0, Math.min(1, (progress - 0.33) / 0.33));
  const phase3T = Math.max(0, Math.min(1, (progress - 0.66) / 0.34));

  return (
    <group>
      {/* Phase 1: pouring water (0..0.33) → right boiling tube */}
      {progress < 0.34 && (
        <group>
          {/* Beaker tilted over the right tube — lifts in, tilts during pour, lifts back */}
          <group
            position={[
              RIGHT_TUBE_TOP[0] + 0.18,
              RIGHT_TUBE_TOP[1] + 0.30,
              RIGHT_TUBE_TOP[2],
            ]}
            rotation={[0, 0, -Math.max(0.15, phase1T) * 1.1]}
          >
            {/* Beaker body */}
            <mesh castShadow>
              <cylinderGeometry args={[0.08, 0.08, 0.13, 28, 1, true]} />
              <meshPhysicalMaterial color="#e7f1fb" transparent opacity={0.55} transmission={0.85} roughness={0.05} ior={1.5} side={THREE.DoubleSide} />
            </mesh>
            {/* Beaker bottom */}
            <mesh position={[0, -0.063, 0]}>
              <cylinderGeometry args={[0.08, 0.08, 0.005, 28]} />
              <meshPhysicalMaterial color="#e7f1fb" transparent opacity={0.6} transmission={0.85} roughness={0.05} />
            </mesh>
            {/* Water still inside the beaker (shrinks as it pours out) */}
            <mesh position={[0, 0.02 - 0.06 * phase1T, 0]}>
              <cylinderGeometry args={[0.075, 0.075, 0.08 * (1 - phase1T) + 0.02, 24]} />
              <meshPhysicalMaterial color="#bfe3ff" transparent opacity={0.85} transmission={0.9} ior={1.33} roughness={0.05} />
            </mesh>
          </group>
          {/* Water stream from beaker lip into the tube opening */}
          {phase1T > 0.15 && phase1T < 0.95 && (
            <mesh position={[RIGHT_TUBE_TOP[0] + 0.02, RIGHT_TUBE_TOP[1] + 0.16, RIGHT_TUBE_TOP[2]]} rotation={[0, 0, 0.15]}>
              <cylinderGeometry args={[0.007, 0.013, 0.30, 12]} />
              <meshPhysicalMaterial color="#bfe3ff" transparent opacity={0.85} transmission={0.92} ior={1.33} roughness={0.05} depthWrite={false} />
            </mesh>
          )}
        </group>
      )}

      {/* Phase 2: dropping zinc granules (0.33..0.66) → left boiling tube */}
      {progress >= 0.33 && progress < 0.67 && (
        <group>
          {[
            { x: -0.012, z: 0.005, delay: 0.0 },
            { x: 0.010, z: -0.006, delay: 0.18 },
            { x: 0.000, z: 0.010, delay: 0.36 },
            { x: -0.006, z: -0.010, delay: 0.54 },
            { x: 0.012, z: 0.006, delay: 0.72 },
          ].map((seed, i) => {
            const localT = Math.max(0, Math.min(1, (phase2T - seed.delay) / 0.28));
            if (localT === 0) return null;
            const startY = LEFT_TUBE_TOP[1] + 0.30;
            const endY = LEFT_TUBE_TOP[1] - 0.35;
            const y = startY + (endY - startY) * easeInOut(localT);
            return (
              <mesh
                key={i}
                position={[LEFT_TUBE_TOP[0] + seed.x, y, LEFT_TUBE_TOP[2] + seed.z]}
                rotation={[localT * 4, localT * 3, 0]}
                castShadow
              >
                <dodecahedronGeometry args={[0.0065, 0]} />
                <meshStandardMaterial color="#9aa0a8" metalness={0.7} roughness={0.4} />
              </mesh>
            );
          })}
          {/* Tweezers / spatula hovering above */}
          <group position={[LEFT_TUBE_TOP[0] + 0.04, LEFT_TUBE_TOP[1] + 0.34, LEFT_TUBE_TOP[2]]} rotation={[0, 0, -0.25]}>
            <mesh castShadow>
              <cylinderGeometry args={[0.003, 0.003, 0.10, 10]} />
              <meshStandardMaterial color="#b8bcc4" metalness={0.8} roughness={0.3} />
            </mesh>
          </group>
        </group>
      )}

      {/* Phase 3: pouring HCl (0.66..1.0) → left boiling tube */}
      {progress >= 0.66 && (
        <group>
          {/* Reagent bottle */}
          <group
            position={[
              LEFT_TUBE_TOP[0] + 0.18,
              LEFT_TUBE_TOP[1] + 0.30,
              LEFT_TUBE_TOP[2],
            ]}
            rotation={[0, 0, -Math.max(0.15, phase3T) * 1.0]}
          >
            {/* Bottle body — amber glass */}
            <mesh castShadow>
              <cylinderGeometry args={[0.028, 0.032, 0.10, 20]} />
              <meshPhysicalMaterial color="#a06030" transparent opacity={0.7} transmission={0.4} roughness={0.3} />
            </mesh>
            {/* Bottle neck */}
            <mesh position={[0, 0.06, 0]}>
              <cylinderGeometry args={[0.012, 0.015, 0.03, 14]} />
              <meshStandardMaterial color="#7a4a20" roughness={0.5} />
            </mesh>
            {/* Visible label */}
            <mesh position={[0, 0, 0.034]}>
              <planeGeometry args={[0.04, 0.05]} />
              <meshStandardMaterial color="#fef6d8" roughness={0.7} />
            </mesh>
          </group>
          {/* HCl stream */}
          {phase3T > 0.15 && phase3T < 0.95 && (
            <mesh position={[LEFT_TUBE_TOP[0] + 0.015, LEFT_TUBE_TOP[1] + 0.14, LEFT_TUBE_TOP[2]]} rotation={[0, 0, 0.12]}>
              <cylinderGeometry args={[0.004, 0.010, 0.32, 12]} />
              <meshPhysicalMaterial color="#fff5d0" transparent opacity={0.85} transmission={0.88} ior={1.34} roughness={0.05} depthWrite={false} />
            </mesh>
          )}
        </group>
      )}
    </group>
  );
}

/**
 * Cotton-bud drying animation — a cotton bud descends to the right-side
 * exit of the glass tube, dabs the opening, and lifts back. The bud's
 * `wet` prop flips on after the dab so the tip looks slightly darker.
 */
export function CottonBudAnimation({ active, progress }: BaseProps) {
  if (!active) return null;
  let t: number;
  if (progress < 0.45) t = easeInOut(progress / 0.45);          // descend
  else if (progress < 0.6) t = 1;                                // dab
  else t = 1 - easeInOut((progress - 0.6) / 0.4);                // lift

  const startY = TUBE_HOME_POS[1] + 0.18;
  const endY = TUBE_HOME_POS[1] + 0.02;
  const y = lerp(startY, endY, t);
  const x = TUBE_HOME_POS[0] + 0.07;
  const z = TUBE_HOME_POS[2];

  return (
    <group position={[x, y, z]} rotation={[0, 0, Math.PI / 6]}>
      <CottonBud wet={progress > 0.6} />
    </group>
  );
}

/**
 * Steam wisps inside the heated section of the glass tube — small
 * upward-rising white particles to communicate that water vapour is
 * being released during reduction.
 */
const STEAM_COUNT = 24;
export function ReductionSteam({
  active,
  position,
}: {
  active: boolean;
  position: [number, number, number];
}) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const seeds = useMemo(() => Array.from({ length: STEAM_COUNT }, () => ({
    speed: 0.18 + Math.random() * 0.22,
    phase: Math.random(),
    sx: (Math.random() - 0.5) * 0.04,
    sz: (Math.random() - 0.5) * 0.012,
    swayPhase: Math.random() * Math.PI * 2,
    size: 0.012 + Math.random() * 0.014,
  })), []);

  useFrame((s) => {
    if (!ref.current) return;
    if (!active) {
      for (let i = 0; i < STEAM_COUNT; i++) {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        ref.current.setMatrixAt(i, dummy.matrix);
      }
      ref.current.instanceMatrix.needsUpdate = true;
      return;
    }
    const t = s.clock.elapsedTime;
    for (let i = 0; i < STEAM_COUNT; i++) {
      const b = seeds[i];
      const cycle = (t * b.speed + b.phase) % 1;
      const y = position[1] + cycle * 0.30;
      const x = position[0] + b.sx + Math.sin(t * 1.6 + b.swayPhase) * 0.01;
      const z = position[2] + b.sz;
      const fade = 1 - cycle;
      dummy.position.set(x, y, z);
      dummy.scale.setScalar(b.size * fade);
      dummy.updateMatrix();
      ref.current.setMatrixAt(i, dummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
  });

  if (!active) return null;
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, STEAM_COUNT]}>
      <sphereGeometry args={[1, 8, 8]} />
      <meshBasicMaterial color="#fafafa" transparent opacity={0.45} depthWrite={false} />
    </instancedMesh>
  );
}

export { CuOPowder };
